import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { MultiSelect } from '@/components/ui/multiselect';
import { Separator } from '@/components/ui/separator';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Filter, X, RotateCcw, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ColumnDefinition } from '@/components/column-configurator-drawer';
import type { Project, Client, Employee, ProjectType, ProjectHealth } from '@/types';
import { PROJECT_STATUS_LABELS } from '@/types';
import { useProjectStatuses } from '@/contexts/ProjectStatusContext';

// ─── Filter value types ───────────────────────────────────────────────

/** A multi-select filter (e.g. Project Type, Status, Health) */
export interface MultiSelectFilterValue {
  kind: 'multi-select';
  selected: string[];
}

/** A numeric range filter (e.g. Budget, Actual Cost) */
export interface NumericRangeFilterValue {
  kind: 'numeric-range';
  operator: 'between' | 'gte' | 'lte' | 'eq';
  min?: number;
  max?: number;
  value?: number;
}

/** A text search filter */
export interface TextFilterValue {
  kind: 'text';
  value: string;
}

export type FilterValue = MultiSelectFilterValue | NumericRangeFilterValue | TextFilterValue;

/** Map of column id  →  filter value */
export type ColumnFilters = Record<string, FilterValue>;

// ─── Column metadata helpers ──────────────────────────────────────────

/** Which columns support multi-select filtering and what are their options */
const MULTI_SELECT_COLUMNS: Record<string, { options: { value: string; label: string }[] }> = {
  'project-type': {
    options: [
      { value: 'fixed-price', label: 'Fixed Price' },
      { value: 'time-and-materials', label: 'Time & Materials' },
      { value: 'retainer', label: 'Retainer' },
    ],
  },
  'project-status': {
    options: Object.entries(PROJECT_STATUS_LABELS).map(([id, label]) => ({ value: id, label })),
  },
  'health-indicator': {
    options: [
      { value: 'green', label: 'Green' },
      { value: 'amber', label: 'Amber' },
      { value: 'red', label: 'Red' },
    ],
  },
  'billable': {
    options: [
      { value: 'true',  label: 'Billable' },
      { value: 'false', label: 'Non-billable' },
    ],
  },
};

/** Maximum absolute value for numeric filter inputs (prevents scientific notation overflow) */
const NUMERIC_FILTER_MAX_VALUE = 999_999_999_999;

/** Column ids that hold numeric / currency data */
const NUMERIC_COLUMNS = new Set([
  // ── Legacy (kept for backwards compatibility with saved filters) ──
  'budget',
  'actual-cost',
  'revenue',
  // ── Sales Budget group ──
  'sales-budget',
  'actual-sales',
  'sales-variance',
  // ── Cost Budget group ──
  'cost-budget',
  'actual-costs',
  'cost-variance',
  // ── Resource Budget group ──
  'resource-budget',
  'actual-hours',
  'hours-variance',
  // ── Other financial columns ──
  'wip-amount',
  'gross-profit',
  'forecast-at-completion',
  'profit-margin',
  'completion-percentage',
  'utilisation-rate',
  'team-members',
  'duration',
  'days-remaining',
]);

/** Column ids that are text-searchable (now rendered as multi-select when options are provided) */
const TEXT_COLUMNS = new Set([
  'project-name',
  'project-code',
  'client-code',
  'client-name',
  'client-contact',
  'project-manager',
  'account-manager',
]);

// ─── Helpers ──────────────────────────────────────────────────────────

function getFilterKind(columnId: string, dynamicOptions?: Record<string, { value: string; label: string }[]>): 'multi-select' | 'numeric-range' | 'text' | null {
  if (MULTI_SELECT_COLUMNS[columnId]) return 'multi-select';
  if (dynamicOptions?.[columnId]) return 'multi-select';
  if (NUMERIC_COLUMNS.has(columnId)) return 'numeric-range';
  if (TEXT_COLUMNS.has(columnId)) return 'text';
  return null;
}

function createDefaultFilter(columnId: string, dynamicOptions?: Record<string, { value: string; label: string }[]>): FilterValue | null {
  const kind = getFilterKind(columnId, dynamicOptions);
  if (!kind) return null;
  switch (kind) {
    case 'multi-select':
      return { kind: 'multi-select', selected: [] };
    case 'numeric-range':
      return { kind: 'numeric-range', operator: 'between' };
    case 'text':
      return { kind: 'text', value: '' };
  }
}

/** Count number of active (non-empty) filters */
export function countActiveFilters(filters: ColumnFilters): number {
  let count = 0;
  for (const f of Object.values(filters)) {
    if (f.kind === 'multi-select' && f.selected.length > 0) count++;
    if (f.kind === 'numeric-range' && (f.min !== undefined || f.max !== undefined || f.value !== undefined)) count++;
    if (f.kind === 'text' && f.value.trim() !== '') count++;
  }
  return count;
}

// ─── Reference data for filter resolution ─────────────────────────────

/** Optional lookup data so filters can resolve IDs to display values */
export interface FilterReferenceData {
  clients?: Client[];
  employees?: Employee[];
}

// ─── Client-side filter application ───────────────────────────────────

/** Extract the raw value for a given column from a project row */
export function getProjectColumnValue(columnId: string, project: Project, referenceData?: FilterReferenceData): string | number | undefined {
  switch (columnId) {
    case 'project-type':
      return project.type;
    case 'project-status':
      return String(project.statusId);
    case 'health-indicator':
      return project.health;
    case 'project-name':
      return project.name;
    case 'project-code':
      return project.code;
    case 'client-code': {
      if (!project.clientIds) return undefined;
      if (!referenceData?.clients) return project.clientIds;
      const ids = project.clientIds.split(',').filter(Boolean);
      return ids
        .map(id => referenceData.clients!.find(c => c.id === parseInt(id, 10))?.code)
        .filter((c): c is string => !!c)
        .join(',');
    }
    case 'client-name':
      return project.clientNames ?? '';
    case 'project-manager': {
      if (!project.projectManagerId || !referenceData?.employees) return undefined;
      return referenceData.employees.find(e => e.id === project.projectManagerId)?.name;
    }
    case 'account-manager': {
      if (!project.accountManagerId || !referenceData?.employees) return undefined;
      return referenceData.employees.find(e => e.id === project.accountManagerId)?.name;
    }
    case 'billable':
      return String(project.billable ?? true);
    // ── Legacy column IDs (kept for backwards compatibility with saved filters) ──
    case 'budget':
      return project.budgetRevenue;
    case 'actual-cost':
      return project.actualLabour + project.actualExpense;
    case 'revenue':
      return project.actualRevenue;
    // ── Sales Budget group ──
    case 'sales-budget':
      return project.budgetRevenue;
    case 'actual-sales':
      return project.actualRevenue;
    case 'sales-variance':
      return project.salesVariance;
    // ── Cost Budget group ──
    case 'cost-budget':
      return project.budgetLabour + project.budgetExpense;
    case 'actual-costs':
      return project.actualLabour + project.actualExpense;
    case 'cost-variance':
      return project.costVariance;
    // ── Resource Budget group ──
    case 'resource-budget':
      return project.budgetHours;
    case 'actual-hours':
      return project.actualHours;
    case 'hours-variance':
      return project.hoursVariance;
    case 'wip-amount':
      return project.wipAmount;
    case 'gross-profit':
      return project.actualRevenue - project.actualLabour - project.actualExpense;
    case 'profit-margin':
      return project.marginPercent;
    case 'completion-percentage':
      return project.completionPercent;
    case 'allocated-hours': // legacy — kept for backwards compatibility
      return project.budgetHours;
    case 'duration': {
      const start = new Date(project.startDate);
      const end = new Date(project.endDate);
      return Math.ceil(Math.abs(end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    }
    case 'days-remaining': {
      const now = new Date();
      const end2 = new Date(project.endDate);
      return Math.ceil((end2.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    }
    default:
      return undefined;
  }
}

/** Columns whose raw value is a comma-separated list of individual tokens */
const MULTI_VALUE_COLUMNS = new Set(['client-code', 'client-name']);

/** Apply all column filters to a list of projects. Returns a filtered copy. */
export function applyColumnFilters(projects: Project[], filters: ColumnFilters, referenceData?: FilterReferenceData): Project[] {
  const activeEntries = Object.entries(filters).filter(([, f]) => {
    if (f.kind === 'multi-select') return f.selected.length > 0;
    if (f.kind === 'numeric-range') return f.min !== undefined || f.max !== undefined || f.value !== undefined;
    if (f.kind === 'text') return f.value.trim() !== '';
    return false;
  });

  if (activeEntries.length === 0) return projects;

  return projects.filter((project) => {
    return activeEntries.every(([colId, filter]) => {
      const raw = getProjectColumnValue(colId, project, referenceData);

      if (filter.kind === 'multi-select') {
        if (raw === undefined) return false; // no value → does not match the selected filter
        const rawStr = String(raw);
        // Handle comma-separated multi-value columns (e.g. client-code, client-name)
        if (MULTI_VALUE_COLUMNS.has(colId)) {
          const values = rawStr.split(',').map(v => v.trim()).filter(Boolean);
          return values.some(v => filter.selected.includes(v));
        }
        return filter.selected.includes(rawStr);
      }

      if (filter.kind === 'numeric-range') {
        if (raw === undefined || typeof raw !== 'number') return false;
        switch (filter.operator) {
          case 'between':
            if (filter.min !== undefined && raw < filter.min) return false;
            if (filter.max !== undefined && raw > filter.max) return false;
            return true;
          case 'gte':
            return filter.value === undefined || raw >= filter.value;
          case 'lte':
            return filter.value === undefined || raw <= filter.value;
          case 'eq':
            return filter.value === undefined || raw === filter.value;
        }
      }

      if (filter.kind === 'text') {
        if (raw === undefined) return false;
        return String(raw).toLowerCase().includes(filter.value.toLowerCase());
      }

      return true;
    });
  });
}

// ─── Component ────────────────────────────────────────────────────────

interface ProjectFilterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Currently visible columns (only these are shown as filter options) */
  visibleColumns: ColumnDefinition[];
  /** Current filter state – lives in the parent so it persists while the sheet closes */
  filters: ColumnFilters;
  /** Called when the user clicks Apply */
  onApply: (filters: ColumnFilters) => void;
  /** Called when the user clicks Clear All */
  onClear: () => void;
  /** Dynamic options for text columns (e.g. project names, client names derived from data) */
  availableOptions?: Record<string, { value: string; label: string }[]>;
}

export function ProjectFilterSheet({
  open,
  onOpenChange,
  visibleColumns,
  filters: appliedFilters,
  onApply,
  onClear,
  availableOptions = {},
}: ProjectFilterSheetProps) {
  const { selectableStatuses } = useProjectStatuses();

  // Build dynamic multi-select columns with org-specific status labels
  const dynamicMultiSelectColumns = useMemo(() => {
    if (selectableStatuses.length === 0) return MULTI_SELECT_COLUMNS;
    return {
      ...MULTI_SELECT_COLUMNS,
      'project-status': {
        options: selectableStatuses.map(s => ({ value: String(s.id), label: s.name })),
      },
    };
  }, [selectableStatuses]);

  // Local draft state so changes aren't applied until the user clicks "Apply"
  const [draft, setDraft] = useState<ColumnFilters>({});

  // Sync draft from applied filters when sheet opens
  useEffect(() => {
    if (open) {
      setDraft({ ...appliedFilters });
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filterable columns = visible columns that have a known filter kind
  const filterableColumns = useMemo(() => {
    return visibleColumns.filter((col) => getFilterKind(col.id, availableOptions) !== null);
  }, [visibleColumns, availableOptions]);

  // ── Draft helpers ──

  const updateDraft = useCallback((columnId: string, value: FilterValue) => {
    setDraft((prev) => ({ ...prev, [columnId]: value }));
  }, []);

  const clearDraft = useCallback(() => {
    setDraft({});
  }, []);

  const draftActiveCount = useMemo(() => countActiveFilters(draft), [draft]);
  const appliedActiveCount = useMemo(() => countActiveFilters(appliedFilters), [appliedFilters]);

  const hasChanges = useMemo(() => {
    return JSON.stringify(draft) !== JSON.stringify(appliedFilters);
  }, [draft, appliedFilters]);

  /** True when any numeric-range filter has min > max */
  const hasRangeErrors = useMemo(() => {
    return Object.values(draft).some((f) => {
      if (f.kind !== 'numeric-range' || f.operator !== 'between') return false;
      return f.min !== undefined && f.max !== undefined && f.min > f.max;
    });
  }, [draft]);

  // ── Handlers ──

  const handleApply = () => {
    onApply(draft);
    onOpenChange(false);
  };

  const handleClearAll = () => {
    clearDraft();
  };

  const handleResetDraft = () => {
    clearDraft();
  };

  // ── Render helpers ──

  const renderMultiSelect = (columnId: string) => {
    const meta = dynamicMultiSelectColumns[columnId];
    const dynamicOpts = availableOptions[columnId];
    const options = meta?.options ?? dynamicOpts;
    if (!options) return null;

    const current = (draft[columnId] as MultiSelectFilterValue | undefined) ?? {
      kind: 'multi-select' as const,
      selected: [],
    };

    return (
      <MultiSelect
        options={options}
        placeholder={`Select ${columnId.replace(/-/g, ' ')}...`}
        emptyMessage="No options found."
        maxDisplay={2}
        value={current.selected}
        onValueChange={(values) =>
          updateDraft(columnId, { kind: 'multi-select', selected: values })
        }
      />
    );
  };

  const renderNumericRange = (columnId: string) => {
    const current = (draft[columnId] as NumericRangeFilterValue | undefined) ?? {
      kind: 'numeric-range' as const,
      operator: 'between' as const,
    };

    const setOperator = (op: NumericRangeFilterValue['operator']) => {
      updateDraft(columnId, { ...current, kind: 'numeric-range', operator: op, min: undefined, max: undefined, value: undefined });
    };

    const rangeInvalid = current.operator === 'between'
      && current.min !== undefined
      && current.max !== undefined
      && current.min > current.max;

    return (
      <div className="space-y-3">
        <Select
          value={current.operator}
          onValueChange={(v) => setOperator(v as NumericRangeFilterValue['operator'])}
        >
          <SelectTrigger className="w-full h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="between">Between</SelectItem>
            <SelectItem value="gte">Greater than or equal</SelectItem>
            <SelectItem value="lte">Less than or equal</SelectItem>
            <SelectItem value="eq">Equal to</SelectItem>
          </SelectContent>
        </Select>

        {current.operator === 'between' ? (
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <Input
                type="number"
                placeholder="Min"
                className={cn('h-8 text-xs', rangeInvalid && 'border-destructive focus-visible:ring-destructive/50')}
                value={current.min ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') {
                    updateDraft(columnId, { ...current, kind: 'numeric-range', min: undefined });
                    return;
                  }
                  const num = Number(raw);
                  if (!isFinite(num) || Math.abs(num) > NUMERIC_FILTER_MAX_VALUE) return;
                  updateDraft(columnId, { ...current, kind: 'numeric-range', min: num });
                }}
              />
              <span className="text-xs text-muted-foreground">to</span>
              <Input
                type="number"
                placeholder="Max"
                className={cn('h-8 text-xs', rangeInvalid && 'border-destructive focus-visible:ring-destructive/50')}
                value={current.max ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') {
                    updateDraft(columnId, { ...current, kind: 'numeric-range', max: undefined });
                    return;
                  }
                  const num = Number(raw);
                  if (!isFinite(num) || Math.abs(num) > NUMERIC_FILTER_MAX_VALUE) return;
                  updateDraft(columnId, { ...current, kind: 'numeric-range', max: num });
                }}
              />
            </div>
            {rangeInvalid && (
              <p className="text-xs text-destructive">Min must be less than or equal to Max</p>
            )}
          </div>
        ) : (
          <Input
            type="number"
            placeholder="Value"
            className="h-8 text-xs"
            value={current.value ?? ''}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') {
                updateDraft(columnId, { ...current, kind: 'numeric-range', value: undefined });
                return;
              }
              const num = Number(raw);
              if (!isFinite(num) || Math.abs(num) > NUMERIC_FILTER_MAX_VALUE) return;
              updateDraft(columnId, { ...current, kind: 'numeric-range', value: num });
            }}
          />
        )}
      </div>
    );
  };

  const renderTextFilter = (columnId: string) => {
    const current = (draft[columnId] as TextFilterValue | undefined) ?? {
      kind: 'text' as const,
      value: '',
    };

    return (
      <Input
        placeholder="Type to filter..."
        className="h-8 text-xs"
        value={current.value}
        onChange={(e) =>
          updateDraft(columnId, { kind: 'text', value: e.target.value })
        }
      />
    );
  };

  const renderFilterControl = (column: ColumnDefinition) => {
    const kind = getFilterKind(column.id, availableOptions);
    switch (kind) {
      case 'multi-select':
        return renderMultiSelect(column.id);
      case 'numeric-range':
        return renderNumericRange(column.id);
      case 'text':
        return renderTextFilter(column.id);
      default:
        return null;
    }
  };

  // Check if a specific column filter is active
  const isColumnFilterActive = (columnId: string): boolean => {
    const f = draft[columnId];
    if (!f) return false;
    if (f.kind === 'multi-select') return f.selected.length > 0;
    if (f.kind === 'numeric-range') return f.min !== undefined || f.max !== undefined || f.value !== undefined;
    if (f.kind === 'text') return f.value.trim() !== '';
    return false;
  };

  // Clear a single column filter
  const clearColumnFilter = (columnId: string) => {
    const defaultVal = createDefaultFilter(columnId, availableOptions);
    if (defaultVal) {
      updateDraft(columnId, defaultVal);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[450px] sm:w-[450px] sm:max-w-none p-0 flex flex-col h-full gap-0"
      >
        {/* ── Fixed Header ── */}
        <SheetHeader className="px-6 pt-4 pb-3 flex-shrink-0 border-b">
          <SheetTitle className="text-base flex items-center gap-2">
            Filters
            <Badge variant="secondary" className="ml-2 flex items-center gap-1.5 px-2.5 py-0.5">
              <Filter className="h-3 w-3" />
              {appliedActiveCount} {appliedActiveCount === 1 ? 'filter' : 'filters'} active
            </Badge>
          </SheetTitle>
        </SheetHeader>

        {/* ── Scrollable Content ── */}
        <ScrollArea className="flex-1 min-h-0">
          <ScrollAreaViewport className="h-full max-h-full">
            <div className="px-6 py-4 space-y-3">
              {filterableColumns.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Filter className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No filterable columns visible</p>
                  <p className="text-xs mt-1">
                    Add columns like Project Type, Status or Budget to the grid via Configure to enable filtering.
                  </p>
                </div>
              ) : (
                filterableColumns.map((column, idx) => {
                  const active = isColumnFilterActive(column.id);
                  return (
                    <div key={column.id}>
                      {idx > 0 && <Separator className="mb-5" />}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs font-semibold">
                            {column.label || column.name}
                          </Label>
                          {active && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs text-muted-foreground"
                              onClick={() => clearColumnFilter(column.id)}
                            >
                              <X className="h-3 w-3 mr-1" />
                              Clear
                            </Button>
                          )}
                        </div>
                        {renderFilterControl(column)}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </ScrollAreaViewport>
          <ScrollAreaScrollbar orientation="vertical" />
        </ScrollArea>

        {/* ── Fixed Footer ── */}
        <Separator className="flex-shrink-0" />
        <SheetFooter className="px-6 py-4 flex-shrink-0 flex-row items-center justify-between sm:justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
            className="gap-2"
            disabled={draftActiveCount === 0}
          >
            <X className="h-4 w-4" />
            Clear All
          </Button>
          <Button onClick={handleApply} className="gap-2" disabled={hasRangeErrors}>
            <Check className="h-4 w-4" />
            Apply Filters
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
